import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiActivity,
  FiClipboard,
  FiPlus,
  FiUser,
  FiBarChart2,
  FiCoffee,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { User, Avaliation, WorkoutPlan } from "../../../types";
import { useAuth } from "../../../context/AuthContext";
import avaliations from "../../../services/avaliations";
import workoutplans from "../../../services/workoutplans";
import AvaliacaoForm from "../../avaliations/AvaliacaoForm";
import utils from "../../../helpers/utils";
import Objects from "../../../constants/Objects";
import Modal from "../../../tailadmin/components/ui/Modal";
import StudentWorkoutPlanForm from "../workout-plans/StudentWorkoutPlanForm";
import StudentProfileTab from "../tabs/StudentProfileTab";
import BodyAvaliationModal from "../../avaliations/BodyAvaliationModal";
import StudentWorkoutTrackingDashboard from "../dashboards/StudentWorkoutTrackingDashboard";
import StudentFoodLogDashboard from "../dashboards/StudentFoodLogDashboard";
import StudentActivityVsNutritionDashboard from "../dashboards/StudentActivityVsNutritionDashboard";
import {
  calculateIMC as calcIMC,
  calculatePercentualGordura,
  calculatePesoMagro,
  calculatePesoGordo,
  calculateDistribuicaoDobras,
  calculateSomatorioPerimetros,
} from "../../../helpers/avaliationCalculations";
import {
  TabButton,
  EmptyState,
  AvaliationCard,
  WorkoutPlanCard,
} from "./StudentManagementModalComponents";
import { safeNumber } from "./StudentManagementModalUtils";
import { vibrateTactile } from "../../../helpers/vibrate";
import Skeleton from "../../ui/Skeleton";

interface StudentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: User | null;
}

interface AvaliationHistoryItem extends Avaliation {}

function getAvaliationTimestamp(avaliation: AvaliationHistoryItem): number {
  const value = avaliation.date || avaliation.registe || "";
  if (!value) return 0;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return time;
}

function getWorkoutTimestamp(plan: WorkoutPlan): number {
  const value = plan.created_at || plan.start_date || "";
  if (!value) return 0;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return time;
}

export default function StudentManagementModal({
  isOpen,
  onClose,
  student,
}: StudentManagementModalProps) {
  const { getUser } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "avaliation" | "workout" | "profile" | "tracking" | "food" | "comparison"
  >("avaliation");
  const [avaliationList, setAvaliationList] = useState<AvaliationHistoryItem[]>(
    [],
  );
  const [workoutList, setWorkoutList] = useState<WorkoutPlan[]>([]);
  const [selectedAvaliation, setSelectedAvaliation] =
    useState<Avaliation | null>(null);
  const [selectedWorkout, setSelectedWorkout] = useState<WorkoutPlan | null>(
    null,
  );
  const [isAvaliationModalOpen, setIsAvaliationModalOpen] = useState(false);
  const [isWorkoutFormOpen, setIsWorkoutFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [isBodyModalOpen, setIsBodyModalOpen] = useState(false);
  const [calculatedMetrics, setCalculatedMetrics] = useState({
    percentualGordura: null as number | null,
    imc: null as number | null,
    pesoMagro: null as number | null,
    pesoGordo: null as number | null,
    distribuicaoMMSS: null as number | null,
    distribuicaoMMII: null as number | null,
    distribuicaoTronco: null as number | null,
    somatorioPerimetros: null as number | null,
  });

  const handleOpenBodyModal = (av: Avaliation) => {
    if (!student) return;

    const fullAvaliation = {
      ...Objects.avalicao,
      ...av,
      user_id: student.id,
      professional_id: getUser()?.id,
    } as Avaliation;

    const gender =
      student.gener === "F" || student.gener === "Feminino" ? "F" : "M";

    const percentualGordura = calculatePercentualGordura(
      fullAvaliation,
      gender,
    );
    const imc = calcIMC(fullAvaliation);
    const pesoMagro = calculatePesoMagro(fullAvaliation, percentualGordura);
    const pesoGordo = calculatePesoGordo(fullAvaliation, pesoMagro);
    const dist = calculateDistribuicaoDobras(fullAvaliation);
    const somaPerimetros = calculateSomatorioPerimetros(fullAvaliation);

    setCalculatedMetrics({
      percentualGordura,
      imc,
      pesoMagro,
      pesoGordo,
      ...dist,
      somatorioPerimetros: somaPerimetros,
    });

    setSelectedAvaliation(fullAvaliation);
    setIsBodyModalOpen(true);
  };

  const loadData = useCallback(async () => {
    if (!student || !isOpen) return;
    const professional = getUser();
    if (!professional) return;

    setLoading(true);
    const avs = await avaliations.listall(
      String(professional.id),
      String(student.id),
    );
    const avHistory = (avs || []) as unknown as AvaliationHistoryItem[];
    const sortedAvs = [...avHistory].sort(
      (a, b) => getAvaliationTimestamp(b) - getAvaliationTimestamp(a),
    );
    setAvaliationList(sortedAvs);

    const plans = await workoutplans.listAll(
      String(professional.id),
      String(student.id),
    );
    const plansData = plans || [];
    const sortedPlans = [...plansData].sort(
      (a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a),
    );
    setWorkoutList(sortedPlans);
    setLoading(false);
  }, [student, isOpen, getUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateTabsScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    if (!isOpen || !student) return;
    const el = tabsScrollRef.current;
    const run = () => {
      updateTabsScrollState();
    };
    run();
    const t = setTimeout(run, 100);
    if (!el) return () => clearTimeout(t);
    const ro = new ResizeObserver(run);
    ro.observe(el);
    el.addEventListener("scroll", run, { passive: true });
    return () => {
      clearTimeout(t);
      ro.disconnect();
      el.removeEventListener("scroll", run);
    };
  }, [isOpen, student, updateTabsScrollState]);

  const scrollTabs = useCallback((direction: "left" | "right") => {
    vibrateTactile();
    const el = tabsScrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.6;
    el.scrollBy({
      left: direction === "left" ? -step : step,
      behavior: "smooth",
    });
  }, []);

  // Previne scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  if (!isOpen || !student) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 sm:backdrop-blur-sm transition-all duration-300 p-0 ${
          isAvaliationModalOpen || isWorkoutFormOpen ? "hidden" : ""
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="relative flex flex-col overflow-hidden max-h-screen h-full w-screen rounded-none bg-white shadow-2xl dark:bg-boxdark transition-all duration-300">
          {/* Header - Mobile First */}
          <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 py-3 sm:px-4 sm:py-4 md:px-6 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

            {/* Botões de ação - Mobile First */}
            <div className="absolute right-2 top-2 sm:right-3 sm:top-3 md:right-6 md:top-6 z-10 flex items-center gap-1 sm:gap-1.5 md:gap-2">
              <button
                onClick={onClose}
                className="flex items-center justify-center rounded-xl min-w-[44px] min-h-[44px] p-2 sm:min-w-0 sm:min-h-0 sm:p-2 text-gray-500 outline-none transition-all active:bg-white active:text-gray-700 active:scale-95 md:hover:bg-white md:hover:text-gray-700 md:hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:active:bg-boxdark dark:active:text-gray-200 md:dark:hover:bg-boxdark md:dark:hover:text-gray-200 touch-manipulation"
                aria-label="Fechar modal"
              >
                <FiX size={20} className="sm:w-[22px] sm:h-[22px]" />
              </button>
            </div>

            {/* Informações do Aluno - Mobile First */}
            <div className="relative flex items-start gap-2 sm:gap-3 pr-14 sm:pr-16 md:pr-24">
              {/* Avatar com Ring - Mobile First */}
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-0.5 sm:-inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-75 blur-sm"></div>
                <div className="relative h-14 w-14 sm:h-16 sm:w-16 overflow-hidden rounded-full border-2 sm:border-3 border-white shadow-xs dark:border-boxdark">
                  <img
                    src={
                      student.photo ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        student.name,
                      )}&background=85368c&color=fff&size=200`
                    }
                    alt={student.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-green-500 p-1 sm:p-1.5 shadow-sm border-2 border-white dark:border-boxdark">
                  <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-white"></div>
                </div>
              </div>

              <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
                <div className="flex flex-col gap-1 sm:gap-2">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white truncate leading-tight">
                    {student.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="inline-flex rounded-full bg-purple-100 px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      {utils.calcularIdade(
                        new Date(student.birth_date || new Date()),
                      )}{" "}
                      anos
                    </span>
                  </div>
                </div>
                <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                  {student.email}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs - Mobile First: pills com scroll horizontal */}
          <div className="relative border-b border-stroke bg-gray-50/50 dark:bg-gray-900/30 dark:border-strokedark">
            {/* Seta esquerda: mais itens à esquerda */}
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => scrollTabs("left")}
                className="absolute left-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 min-w-[44px] min-h-[44px] rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-200 dark:hover:border-purple-700/50 hover:shadow-lg active:scale-95 transition-all duration-200 sm:hidden touch-manipulation"
                aria-label="Ver mais abas à esquerda"
              >
                <FiChevronLeft className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
              </button>
            )}
            {/* Seta direita: mais itens à direita */}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => scrollTabs("right")}
                className="absolute right-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 min-w-[44px] min-h-[44px] rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-md text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-200 dark:hover:border-purple-700/50 hover:shadow-lg active:scale-95 transition-all duration-200 sm:hidden touch-manipulation"
                aria-label="Ver mais abas à direita"
              >
                <FiChevronRight className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
              </button>
            )}
            {/* Fade nas bordas (mobile) para indicar scroll */}
            <div className="absolute left-0 top-0 bottom-0 w-6 sm:w-8 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-900/30 pointer-events-none z-10 sm:hidden" aria-hidden />
            <div className="absolute right-0 top-0 bottom-0 w-6 sm:w-8 bg-gradient-to-l from-gray-50 to-transparent dark:from-gray-900/30 pointer-events-none z-10 sm:hidden" aria-hidden />

            <div
              ref={tabsScrollRef}
              className="overflow-x-auto scrollbar-hide overflow-y-hidden -webkit-overflow-scrolling-touch snap-x snap-mandatory scroll-smooth px-3 py-3 sm:px-4 sm:py-3 md:px-6"
            >
              <div className="flex gap-2 sm:gap-2.5 md:gap-3 min-w-max sm:min-w-0">
                <TabButton
                  active={activeTab === "profile"}
                  onClick={() => {
                    setActiveTab("profile");
                    setIsAvaliationModalOpen(false);
                    setIsWorkoutFormOpen(false);
                  }}
                  icon={FiUser}
                  label="Perfil"
                />
                <TabButton
                  active={activeTab === "avaliation"}
                  onClick={() => {
                    setActiveTab("avaliation");
                    setIsWorkoutFormOpen(false);
                  }}
                  icon={FiActivity}
                  label="Avaliações"
                  count={avaliationList.length}
                />
                <TabButton
                  active={activeTab === "workout"}
                  onClick={() => {
                    setActiveTab("workout");
                    setIsAvaliationModalOpen(false);
                  }}
                  icon={FiClipboard}
                  label="Treinos"
                  count={workoutList.length}
                />
                <TabButton
                  active={activeTab === "tracking"}
                  onClick={() => {
                    setActiveTab("tracking");
                    setIsAvaliationModalOpen(false);
                    setIsWorkoutFormOpen(false);
                  }}
                  icon={FiBarChart2}
                  label="Atividades"
                />
                <TabButton
                  active={activeTab === "food"}
                  onClick={() => {
                    setActiveTab("food");
                    setIsAvaliationModalOpen(false);
                    setIsWorkoutFormOpen(false);
                  }}
                  icon={FiCoffee}
                  label="Alimentação"
                />
                <TabButton
                  active={activeTab === "comparison"}
                  onClick={() => {
                    setActiveTab("comparison");
                    setIsAvaliationModalOpen(false);
                    setIsWorkoutFormOpen(false);
                  }}
                  icon={FiTrendingUp}
                  label="Comparativo"
                />
              </div>
            </div>
          </div>

          {/* Conteúdo - Mobile First */}
          <div className="flex-1 overflow-y-auto bg-gray-50/50 p-3 sm:p-4 md:p-6 dark:bg-gray-900/20 overscroll-contain">
            {/* Tab: Avaliações - Mobile First */}
            {activeTab === "avaliation" &&
              (loading ? (
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
                    <div>
                      <Skeleton className="h-6 sm:h-7 md:h-8 w-48 sm:w-56 md:w-64 mb-2" />
                      <Skeleton className="h-4 sm:h-5 w-64 sm:w-72 md:w-80" />
                    </div>
                    <Skeleton className="min-h-[48px] w-full rounded-xl sm:min-h-0 sm:h-10 sm:w-40 sm:rounded-xl" />
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-4 md:p-5 sm:rounded-2xl"
                      >
                        <div className="mb-3 sm:mb-4">
                          <Skeleton className="h-5 sm:h-6 w-32 sm:w-40 mb-2" />
                          <Skeleton className="h-4 w-24 sm:w-28" />
                        </div>
                        <div className="space-y-2 sm:space-y-3">
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-20 sm:w-24" />
                            <Skeleton className="h-4 w-16 sm:w-20" />
                          </div>
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-24 sm:w-28" />
                            <Skeleton className="h-4 w-16 sm:w-20" />
                          </div>
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-20 sm:w-24" />
                            <Skeleton className="h-4 w-16 sm:w-20" />
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2 sm:gap-3">
                          <Skeleton className="h-8 flex-1 rounded-lg sm:h-9 sm:rounded-xl" />
                          <Skeleton className="h-8 w-8 rounded-lg sm:h-9 sm:w-9 sm:rounded-xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
                      <div>
                        <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                          Histórico de Avaliações
                        </h4>
                        <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Acompanhe a evolução física do aluno
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedAvaliation(null);
                          setIsAvaliationModalOpen(true);
                        }}
                        className="w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-4 sm:min-h-0 sm:px-5 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98] md:hover:shadow-xl touch-manipulation"
                      >
                        <FiPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                        Nova Avaliação
                      </button>
                    </div>

                    {avaliationList.length === 0 ? (
                      <EmptyState
                        icon={FiActivity}
                        title="Nenhuma avaliação encontrada"
                        description="Comece criando a primeira avaliação para este aluno"
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                        {[...avaliationList]
                          .sort(
                            (a, b) =>
                              getAvaliationTimestamp(b) -
                              getAvaliationTimestamp(a),
                          )
                          .map((av) => {
                            const pesoNum = safeNumber(av.peso);
                            const pesoDisplay =
                              pesoNum && !Number.isNaN(pesoNum)
                                ? `${pesoNum}kg`
                                : "-";
                            const imcNum = calcIMC(av as Avaliation);
                            const imcDisplay =
                              imcNum !== null && !Number.isNaN(imcNum)
                                ? imcNum.toFixed(1)
                                : "-";

                            return (
                              <AvaliationCard
                                key={av.id}
                                avaliation={av}
                                pesoDisplay={pesoDisplay}
                                imcDisplay={imcDisplay}
                                onView={() => handleOpenBodyModal(av)}
                                onEdit={() => {
                                  setSelectedAvaliation({
                                    ...Objects.avalicao,
                                    ...av,
                                    user_id: student.id,
                                    professional_id: getUser()?.id,
                                  } as Avaliation);
                                  setIsAvaliationModalOpen(true);
                                }}
                                onDelete={() => {
                                  if (
                                    window.confirm(
                                      "Tem certeza que deseja excluir esta avaliação?",
                                    )
                                  ) {
                                    avaliations
                                      .remove(String(av.id))
                                      .then(() => loadData());
                                  }
                                }}
                              />
                            );
                          })}
                      </div>
                    )}
                  </div>
                </>
              ))}

            {/* Tab: Planos de Treino - Mobile First */}
            {activeTab === "workout" &&
              (loading ? (
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
                    <div>
                      <Skeleton className="h-6 sm:h-7 md:h-8 w-48 sm:w-56 md:w-64 mb-2" />
                      <Skeleton className="h-4 sm:h-5 w-64 sm:w-72 md:w-80" />
                    </div>
                    <Skeleton className="min-h-[48px] w-full rounded-xl sm:min-h-0 sm:h-10 sm:w-40 sm:rounded-xl" />
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-4 md:p-5 sm:rounded-2xl"
                      >
                        <div className="mb-3 sm:mb-4">
                          <Skeleton className="h-6 sm:h-7 w-3/4 sm:w-2/3 mb-2" />
                          <Skeleton className="h-4 w-1/2 sm:w-1/3" />
                        </div>
                        <div className="space-y-2 sm:space-y-3">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-5/6" />
                          <Skeleton className="h-4 w-4/6" />
                        </div>
                        <div className="mt-4 flex gap-2 sm:gap-3">
                          <Skeleton className="h-8 flex-1 rounded-lg sm:h-9 sm:rounded-xl" />
                          <Skeleton className="h-8 w-8 rounded-lg sm:h-9 sm:w-9 sm:rounded-xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
                      <div>
                        <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                          Planos de Treino
                        </h4>
                        <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          Gerencie os treinos personalizados
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedWorkout(null);
                          setIsWorkoutFormOpen(true);
                        }}
                        className="w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-4 sm:min-h-0 sm:px-5 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.98] md:hover:shadow-xl touch-manipulation"
                      >
                        <FiPlus size={18} className="sm:w-5 sm:h-5" />
                        Novo Plano
                      </button>
                    </div>

                    {workoutList.length === 0 ? (
                      <EmptyState
                        icon={FiClipboard}
                        title="Nenhum plano de treino encontrado"
                        description="Crie o primeiro plano de treino personalizado"
                      />
                    ) : (
                      <div className="space-y-3 sm:space-y-4">
                        {workoutList.map((plan) => (
                          <WorkoutPlanCard
                            key={plan.id}
                            plan={plan}
                            onEdit={() => {
                              setSelectedWorkout(plan);
                              setIsWorkoutFormOpen(true);
                            }}
                            onDelete={async () => {
                              if (
                                window.confirm(
                                  "Tem certeza que deseja excluir este plano?",
                                )
                              ) {
                                await workoutplans.delete_plan(String(plan.id));
                                loadData();
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ))}

            {/* Tab: Acompanhamento de Treinos */}
            {activeTab === "tracking" && (
              <StudentWorkoutTrackingDashboard
                studentId={String(student.id)}
                professionalId={String(getUser()?.id)}
              />
            )}

            {/* Tab: Acompanhamento de Alimentação */}
            {activeTab === "food" && (
              <StudentFoodLogDashboard studentId={String(student.id)} />
            )}

            {/* Tab: Comparativo Atividade vs Alimentação */}
            {activeTab === "comparison" && (
              <StudentActivityVsNutritionDashboard
                studentId={String(student.id)}
                professionalId={String(getUser()?.id)}
              />
            )}

            {/* Tab: Perfil */}
            {activeTab === "profile" &&
              (loading ? (
                <div className="space-y-3 sm:space-y-4 md:space-y-6">
                  <div className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-4 md:p-6 sm:rounded-2xl">
                    <div className="mb-4 sm:mb-6">
                      <Skeleton className="h-7 sm:h-8 w-40 sm:w-48 mb-3" />
                      <Skeleton className="h-4 w-64 sm:w-80" />
                    </div>
                    <div className="space-y-4 sm:space-y-5">
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 sm:gap-4"
                        >
                          <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-32 sm:w-40" />
                            <Skeleton className="h-3 w-48 sm:w-56" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:p-4 md:p-6 sm:rounded-2xl">
                    <Skeleton className="h-7 sm:h-8 w-40 sm:w-48 mb-4" />
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <Skeleton className="h-4 w-20 sm:w-24" />
                          <Skeleton className="h-6 w-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <StudentProfileTab
                  student={student}
                  professionalId={String(getUser()?.id)}
                  onClose={onClose}
                />
              ))}
          </div>
        </div>
      </div>

      {isAvaliationModalOpen && (
        <AvaliacaoForm
          isOpen={isAvaliationModalOpen}
          onBack={() => {
            setIsAvaliationModalOpen(false);
            loadData();
          }}
          data={
            {
              ...selectedAvaliation,
              user_id: student.id,
              professional_id: getUser()?.id,
            } as Avaliation
          }
        />
      )}

      {isBodyModalOpen && selectedAvaliation && (
        <BodyAvaliationModal
          isOpen={isBodyModalOpen}
          onClose={() => setIsBodyModalOpen(false)}
          studentName={student.name}
          professionalName={getUser()?.name || "Profissional"}
          data={selectedAvaliation}
          calculated={calculatedMetrics}
          date={selectedAvaliation.registe || selectedAvaliation.date}
        />
      )}

      {isWorkoutFormOpen && (
        <Modal
          isOpen={isWorkoutFormOpen}
          onClose={() => setIsWorkoutFormOpen(false)}
          isExpanded
          className="!p-0 [&>button]:hidden"
        >
          <StudentWorkoutPlanForm
            initialData={selectedWorkout}
            studentId={String(student.id)}
            professionalId={String(getUser()?.id)}
            onClose={() => setIsWorkoutFormOpen(false)}
            onSuccess={() => {
              setIsWorkoutFormOpen(false);
              loadData();
            }}
          />
        </Modal>
      )}
    </>,
    document.body,
  );
}
